import React, { useMemo, useState } from 'react';
import { Alert, Button, InputNumber, Modal, Select, Typography } from 'antd';
import { CalculatorOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { localeForLanguage } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import {
  asQuantityUnit,
  buildQuantitySetFromPair,
  COMMERCIAL_PRICING_CONVERSION_UNITS,
  COMMERCIAL_UNITS,
  CommercialQuantityValue,
  convertCommercialQuantity,
  convertCommercialQuantityUsingSnapshot,
  getQuantitySetValue,
  hasPersistedQuantitySet,
  isAllocationUnit,
  quantitySetFromFields,
  quantitySetToFields,
  ACTIVE_QUANTITY_UNITS,
  roundCommercialQuantity,
  scaleQuantitySetToAmount,
} from '../../../utils/commercialQuantity';
import type { QuantityUnit } from '../../../utils/commercialQuantity';
import type { AllocationUnit, ConversionSource } from '../../../utils/dimensionConversion';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';
import './CommercialQuantityEditor.css';

type ConversionMode = 'conversion' | 'none';
type PieceRounding = 'none' | 'down' | 'up';

interface CommercialQuantityEditorProps extends ConversionSource {
  value: CommercialQuantityValue;
  onChange: (value: CommercialQuantityValue) => void;
  maxPhysicalQuantity: number;
  maxPricingQuantity: number;
  disabled?: boolean;
  compact?: boolean;
  /**
   * Initial unit for legacy callers that have not persisted a line unit yet.
   * Once a value exists, `value.physicalUnit` is authoritative for this line.
   */
  physicalUnit?: QuantityUnit;
  /**
   * Keep the physical stock quantity fixed. Used by PZ lines traced to a
   * supplier order: the operator may change the commercial unit and price
   * basis, but the received quantity must continue to match its source line.
   */
  lockPhysicalQuantity?: boolean;
  /** Optional context-specific subset of dimensional conversion targets. */
  pricingConversionUnits?: readonly AllocationUnit[];
  /** Optional context-specific units the operator may enter on the left side. */
  sourceUnitOptions?: readonly QuantityUnit[];
  triggerOnly?: boolean;
  triggerLabel?: React.ReactNode;
  lockConversionMode?: boolean;
  lockPricingUnit?: boolean;
  modalDescription?: React.ReactNode;
  modalTitle?: React.ReactNode;
  allowOutOfBounds?: boolean;
  outOfBoundsMessage?: React.ReactNode;
  allowZero?: boolean;
  onZero?: () => void;
}

const positive = (value: unknown, fallback = 1) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export const getModalPopupContainer = (triggerNode: HTMLElement): HTMLElement => (
  triggerNode.closest<HTMLElement>('.ant-modal-content')
  || triggerNode.closest<HTMLElement>('.ant-modal')
  || document.body
);

const CommercialQuantityEditor: React.FC<CommercialQuantityEditorProps> = ({
  value,
  onChange,
  maxPhysicalQuantity,
  maxPricingQuantity,
  disabled = false,
  compact = false,
  physicalUnit = 'pcs',
  lockPhysicalQuantity = false,
  pricingConversionUnits = COMMERCIAL_PRICING_CONVERSION_UNITS,
  sourceUnitOptions = ACTIVE_QUANTITY_UNITS,
  triggerOnly = false,
  triggerLabel,
  lockConversionMode = false,
  lockPricingUnit = false,
  modalDescription,
  modalTitle,
  allowOutOfBounds = false,
  outOfBoundsMessage,
  allowZero = false,
  onZero,
  width,
  length,
  height,
  thickness,
}) => {
  const { t, i18n } = useTranslation(['orders', 'common']);
  // A few isolated consumers/tests provide only `t`. Polish is the product
  // language and is also the safe fallback during i18n bootstrap.
  const language = i18n?.language || 'pl';
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<ConversionMode>('none');
  const [pricingUnit, setPricingUnit] = useState<AllocationUnit>('m2');
  const [priceUnit, setPriceUnit] = useState<QuantityUnit>('pcs');
  const [plainUnit, setPlainUnit] = useState<QuantityUnit>('pcs');
  const [amount, setAmount] = useState(1);
  const [pieceRounding, setPieceRounding] = useState<PieceRounding>('none');
  const [editorDirty, setEditorDirty] = useState(false);

  const dimensions = useMemo(() => ({ width, length, height, thickness }), [height, length, thickness, width]);
  const hasStoredQuantitySet = useMemo(
    () => hasPersistedQuantitySet(value as unknown as Record<string, unknown>),
    [value],
  );
  const storedQuantitySet = useMemo(
    () => hasStoredQuantitySet
      ? quantitySetFromFields(value as unknown as Record<string, unknown>)
      : null,
    [hasStoredQuantitySet, value],
  );
  const persistedQuantitySet = useMemo(
    () => buildQuantitySetFromPair(value, dimensions),
    [dimensions, value],
  );
  const displayConversionUnit = value.conversionUnit
    ?? (value.physicalUnit !== value.pricingUnit ? value.pricingUnit : null);
  const derivedDisplayConversionQuantity = useMemo(() => {
    if (!displayConversionUnit || displayConversionUnit === value.physicalUnit) return undefined;
    if (!isAllocationUnit(value.physicalUnit) || !isAllocationUnit(displayConversionUnit)) return undefined;
    if (hasStoredQuantitySet) {
      return getQuantitySetValue(storedQuantitySet!, displayConversionUnit) ?? undefined;
    }
    return convertCommercialQuantity(
      value.physicalQuantity,
      value.physicalUnit,
      displayConversionUnit,
      dimensions,
    ).value ?? undefined;
  }, [dimensions, displayConversionUnit, hasStoredQuantitySet, storedQuantitySet, value.physicalQuantity, value.physicalUnit]);
  const displayConversionQuantity = hasStoredQuantitySet
    ? derivedDisplayConversionQuantity
    : value.conversionQuantity
      ?? (displayConversionUnit === value.pricingUnit ? value.pricingQuantity : undefined)
      ?? derivedDisplayConversionQuantity;
  const isConverted = Boolean(displayConversionUnit && displayConversionUnit !== value.physicalUnit);
  const legacyUnitReadOnly = !isAllocationUnit(value.physicalUnit);
  // Converting a unit into itself is not a conversion. Every dimensional unit
  // can be the entered source and every other dimensional unit can be its view.
  const activePhysicalUnit = plainUnit;
  const canConvertPhysicalUnit = isAllocationUnit(activePhysicalUnit);
  const pricingUnitOptions = useMemo(
    () => canConvertPhysicalUnit
      ? pricingConversionUnits.filter((unit) => (
        unit !== activePhysicalUnit
        && (!storedQuantitySet || getQuantitySetValue(storedQuantitySet, unit) !== null)
      ))
      : [],
    [activePhysicalUnit, canConvertPhysicalUnit, pricingConversionUnits, storedQuantitySet],
  );
  const activeConversionUnit = pricingUnitOptions.includes(pricingUnit)
    ? pricingUnit
    : (pricingUnitOptions[0] ?? 'm2');

  const openEditor = () => {
    // The persisted line pair is the only initial state. Catalog metadata is
    // deliberately not consulted: choosing m³ here means the amount below is
    // entered in m³, regardless of how this catalog item was created years ago.
    const storedConversionUnit = value.conversionUnit
      ?? (value.pricingUnit !== value.physicalUnit ? value.pricingUnit : null);
    const nextPhysicalUnit = asQuantityUnit(value.physicalUnit, physicalUnit);
    const nextCanConvertPhysicalUnit = isAllocationUnit(nextPhysicalUnit);
    const nextPricingUnitOptions = nextCanConvertPhysicalUnit
      ? pricingConversionUnits.filter((unit) => (
        unit !== nextPhysicalUnit
        && (!storedQuantitySet || getQuantitySetValue(storedQuantitySet, unit) !== null)
      ))
      : [];
    const nextMode: ConversionMode = nextCanConvertPhysicalUnit && nextPricingUnitOptions.length > 0 && (lockConversionMode || (value.physicalUnit === nextPhysicalUnit
      && storedConversionUnit !== null
      && storedConversionUnit !== value.physicalUnit))
      ? 'conversion'
      : 'none';
    const fallbackPricingUnit = nextPricingUnitOptions[0] ?? 'm2';
    const nextConversionUnit = (
      storedConversionUnit
        && isAllocationUnit(storedConversionUnit)
        && nextPricingUnitOptions.includes(storedConversionUnit)
        ? storedConversionUnit
        : fallbackPricingUnit
    );
    setMode(nextMode);
    setPricingUnit(nextConversionUnit);
    const storedPriceUnit = asQuantityUnit(value.pricingUnit, nextPhysicalUnit);
    setPriceUnit(
      storedQuantitySet && isAllocationUnit(storedPriceUnit)
        && getQuantitySetValue(storedQuantitySet, storedPriceUnit) === null
        ? nextPhysicalUnit
        : storedPriceUnit,
    );
    setPlainUnit(nextPhysicalUnit);
    setAmount(positive(value.physicalQuantity));
    setPieceRounding('none');
    setEditorDirty(false);
    setOpen(true);
  };

  const baseResult = useMemo(() => {
    const physicalQuantity = positive(amount);
    const pristinePersistedPair = !editorDirty
      && activePhysicalUnit === value.physicalUnit
      && roundCommercialQuantity(physicalQuantity) === roundCommercialQuantity(value.physicalQuantity);
    const displayConversion = mode === 'conversion'
      ? convertCommercialQuantityUsingSnapshot(
        physicalQuantity,
        activePhysicalUnit as AllocationUnit,
        activeConversionUnit,
        dimensions,
        storedQuantitySet,
      )
      : null;
    const priceConversion = priceUnit === activePhysicalUnit
      ? { value: physicalQuantity, error: null }
      : isAllocationUnit(activePhysicalUnit) && isAllocationUnit(priceUnit)
        ? convertCommercialQuantityUsingSnapshot(physicalQuantity, activePhysicalUnit, priceUnit, dimensions, storedQuantitySet)
        : { value: null, error: 'unsupported-unit-pair' };
    return {
      physicalQuantity,
      physicalUnit: activePhysicalUnit,
      pricingQuantity: pristinePersistedPair && priceUnit === value.pricingUnit
        ? value.pricingQuantity
        : priceConversion.value ?? 0,
      pricingUnit: priceUnit,
      conversionQuantity: pristinePersistedPair
        && value.conversionUnit === activeConversionUnit
        && value.conversionQuantity !== undefined
        ? value.conversionQuantity
        : displayConversion?.value ?? undefined,
      conversionExactQuantity: pristinePersistedPair
        && value.conversionUnit === activeConversionUnit
        && value.conversionQuantity !== undefined
        ? value.conversionQuantity
        : displayConversion?.exactValue ?? undefined,
      conversionUnit: mode === 'conversion' ? activeConversionUnit : null,
      error: displayConversion?.error || priceConversion.error,
    };
  }, [activeConversionUnit, activePhysicalUnit, amount, dimensions, editorDirty, mode, priceUnit, storedQuantitySet, value]);

  const fractionalPieceRounding = useMemo(() => {
    const sourceCanProducePieces = activePhysicalUnit === 'm2' || activePhysicalUnit === 'm3';
    const pristinePieces = !editorDirty && value.conversionUnit === 'pcs'
      ? value.conversionQuantity
      : (!editorDirty && value.pricingUnit === 'pcs' ? value.pricingQuantity : undefined);
    const pieceConversion = sourceCanProducePieces
      ? convertCommercialQuantityUsingSnapshot(
        positive(amount),
        activePhysicalUnit,
        'pcs',
        dimensions,
        storedQuantitySet,
      )
      : null;
    const exactPieces = Number(pristinePieces ?? pieceConversion?.exactValue);
    const hasFraction = Number.isFinite(exactPieces)
      && Math.abs(exactPieces - Math.round(exactPieces)) > 0.0001;
    if (
      lockPhysicalQuantity
      || !sourceCanProducePieces
      || baseResult.error
      || !hasFraction
    ) return null;

    const buildOption = (pieces: number) => {
      if (pieces <= 0) return null;
      const source = convertCommercialQuantityUsingSnapshot(
        pieces,
        'pcs',
        activePhysicalUnit,
        dimensions,
        storedQuantitySet,
      );
      const pricing = priceUnit === 'pcs'
        ? { value: pieces, error: null }
        : convertCommercialQuantityUsingSnapshot(pieces, 'pcs', priceUnit as AllocationUnit, dimensions, storedQuantitySet);
      if (source.error || pricing.error || source.value === null || pricing.value === null) return null;
      return {
        pieces,
        physicalQuantity: source.value,
        pricingQuantity: pricing.value,
      };
    };

    return {
      exactPieces,
      down: buildOption(Math.floor(exactPieces)),
      up: buildOption(Math.ceil(exactPieces)),
    };
  }, [
    activePhysicalUnit,
    amount,
    baseResult.error,
    dimensions,
    editorDirty,
    lockPhysicalQuantity,
    priceUnit,
    storedQuantitySet,
    value.conversionQuantity,
    value.conversionUnit,
    value.pricingQuantity,
    value.pricingUnit,
  ]);

  const result = useMemo(() => {
    if (!fractionalPieceRounding || pieceRounding === 'none') return baseResult;
    const selected = fractionalPieceRounding[pieceRounding];
    if (!selected) return baseResult;
    const roundedDisplayConversion = mode === 'conversion'
      ? convertCommercialQuantityUsingSnapshot(
        selected.pieces,
        'pcs',
        activeConversionUnit,
        dimensions,
        storedQuantitySet,
      )
      : null;
    return {
      ...baseResult,
      physicalQuantity: selected.physicalQuantity,
      pricingQuantity: selected.pricingQuantity,
      conversionQuantity: mode === 'conversion'
        ? roundedDisplayConversion?.value ?? baseResult.conversionQuantity
        : undefined,
      conversionExactQuantity: mode === 'conversion'
        ? roundedDisplayConversion?.exactValue ?? baseResult.conversionExactQuantity
        : undefined,
    };
  }, [activeConversionUnit, baseResult, dimensions, fractionalPieceRounding, mode, pieceRounding, storedQuantitySet]);

  const formatQuantity = (quantity: number) => Number(quantity).toLocaleString(
    localeForLanguage(language),
    { maximumFractionDigits: 3 },
  );

  const amountMaximum = Math.max(maxPhysicalQuantity, maxPricingQuantity);
  const isZeroAmount = allowZero && Boolean(onZero) && Number(amount) === 0;
  const resultOutOfBounds = result.physicalQuantity > maxPhysicalQuantity
    || result.pricingQuantity > maxPricingQuantity
    || Number(result.conversionQuantity || 0) > maxPricingQuantity;
  const canApply = !legacyUnitReadOnly && (isZeroAmount || (!result.error
    && (!resultOutOfBounds || allowOutOfBounds)
    && result.physicalQuantity > 0
    && result.pricingQuantity > 0
    && result.physicalQuantity <= amountMaximum));

  const handleApply = () => {
    if (!canApply) return;
    if (isZeroAmount) {
      onZero?.();
      setOpen(false);
      return;
    }
    const selectedRoundedPieces = pieceRounding === 'none'
      ? null
      : fractionalPieceRounding?.[pieceRounding]?.pieces ?? null;
    const appliedValue: CommercialQuantityValue = {
      physicalQuantity: roundCommercialQuantity(result.physicalQuantity),
      physicalUnit: result.physicalUnit,
      pricingQuantity: roundCommercialQuantity(result.pricingQuantity),
      pricingUnit: result.pricingUnit,
      ...(mode === 'conversion'
        ? {
          conversionQuantity: roundCommercialQuantity(result.conversionQuantity || 0),
          conversionUnit: result.conversionUnit,
        }
        : selectedRoundedPieces !== null
          ? {
            conversionQuantity: roundCommercialQuantity(selectedRoundedPieces),
            conversionUnit: 'pcs',
          }
          : { conversionUnit: null }),
    };
    const appliedSet = storedQuantitySet && isAllocationUnit(appliedValue.physicalUnit)
      ? scaleQuantitySetToAmount(storedQuantitySet, appliedValue.physicalQuantity, appliedValue.physicalUnit)
      : buildQuantitySetFromPair(appliedValue, dimensions);
    onChange({
      ...appliedValue,
      // Existing lines propagate their canonical snapshot through the editor.
      // New drafts keep the legacy callback shape; their owning adapter builds
      // the first set from the accepted pair.
      ...(storedQuantitySet && appliedSet ? quantitySetToFields(appliedSet) : {}),
    });
    setOpen(false);
  };

  return (
    <>
      {triggerOnly ? (!disabled && (
        <Button type="default" size="small" icon={<CalculatorOutlined />} onClick={openEditor}>
          {triggerLabel || t('orders:unitConversion.convert')}
        </Button>
      )) : <div className={`commercial-quantity${compact ? ' commercial-quantity--compact' : ''}`}>
        {isConverted ? (
          <div className="commercial-quantity__chain">
            <div><span>{t('orders:unitConversion.physicalQuantity')}</span><strong>{formatQuantity(value.physicalQuantity)} {formatUnit(value.physicalUnit, language)}</strong></div>
            <RightOutlined />
            <div className="commercial-quantity__pricing"><span>{t('orders:unitConversion.convertedQuantity')}</span><strong>{displayConversionQuantity == null ? '—' : formatQuantity(displayConversionQuantity)} {formatUnit(displayConversionUnit ?? value.pricingUnit, language)}</strong></div>
          </div>
        ) : (
          <strong className="commercial-quantity__plain">
            {formatQuantity(value.physicalQuantity)} {formatUnit(value.physicalUnit, language)}
          </strong>
        )}
        {!disabled && (
          <Button type="link" size="small" icon={<CalculatorOutlined />} onClick={openEditor}>
            {isConverted ? t('orders:unitConversion.change') : t('orders:unitConversion.configure')}
          </Button>
        )}
      </div>}

      <Modal
        open={open}
        width={720}
        title={modalTitle || t('orders:unitConversion.title')}
        okText={t('orders:unitConversion.apply')}
        cancelText={t('common:cancel')}
        okButtonProps={{ disabled: !canApply }}
        onOk={handleApply}
        onCancel={() => setOpen(false)}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary" className="commercial-quantity-modal__intro">
          {modalDescription || t('orders:unitConversion.description')}
        </Typography.Paragraph>

        {lockPhysicalQuantity && (
          <Alert
            type="info"
            showIcon
            message={t('orders:unitConversion.physicalQuantityLocked')}
          />
        )}

        {legacyUnitReadOnly && (
          <Alert
            type="warning"
            showIcon
            message={t('orders:unitConversion.legacyUnitReadOnly')}
          />
        )}

        <div className="commercial-quantity-modal__simple-grid">
          <label>
            <span>{t('orders:unitConversion.baseUnit')}</span>
            <Select
              data-testid="conversion-base-unit"
              value={activePhysicalUnit}
              disabled={legacyUnitReadOnly}
              getPopupContainer={getModalPopupContainer}
              options={(legacyUnitReadOnly
                ? [value.physicalUnit]
                : sourceUnitOptions.filter((unit) => (
                  !hasStoredQuantitySet
                  || !isAllocationUnit(unit)
                  || getQuantitySetValue(storedQuantitySet!, unit) !== null
                )))
                .map((unit) => ({ value: unit, label: formatUnit(unit, language) }))}
              onChange={(next) => {
                const previousUnit = activePhysicalUnit;
                const persistedPreviousAmount = persistedQuantitySet && isAllocationUnit(previousUnit)
                  ? getQuantitySetValue(persistedQuantitySet, previousUnit)
                  : null;
                const persistedNextAmount = persistedQuantitySet && isAllocationUnit(next)
                  ? getQuantitySetValue(persistedQuantitySet, next)
                  : null;
                const convertedNextAmount = isAllocationUnit(previousUnit) && isAllocationUnit(next)
                  ? convertCommercialQuantityUsingSnapshot(
                    positive(amount),
                    previousUnit,
                    next,
                    dimensions,
                    storedQuantitySet,
                  ).value
                  : null;
                const nextAmount = persistedPreviousAmount !== null
                  && persistedNextAmount !== null
                  && roundCommercialQuantity(positive(amount)) === persistedPreviousAmount
                  ? persistedNextAmount
                  : convertedNextAmount;
                setPlainUnit(next);
                setAmount(positive(nextAmount, positive(amount)));
                setPieceRounding('none');
                setEditorDirty(true);
                if (!isAllocationUnit(next)) {
                  setMode('none');
                  setPriceUnit(next);
                }
                if (pricingUnit === next) {
                  setPricingUnit(COMMERCIAL_UNITS.find((unit) => unit !== next) || 'pcs');
                }
              }}
            />
          </label>
          <label>
            <span>{t('orders:unitConversion.convertTo')}</span>
            <Select
              data-testid="conversion-target-unit"
              value={mode === 'none' ? 'none' : activeConversionUnit}
              disabled={lockConversionMode || legacyUnitReadOnly}
              getPopupContainer={getModalPopupContainer}
              options={[
                { value: 'none', label: t('orders:unitConversion.noConversionShort') },
                ...pricingUnitOptions.map((unit) => ({ value: unit, label: formatUnit(unit, language) })),
              ]}
              onChange={(next) => {
                setPieceRounding('none');
                setEditorDirty(true);
                if (next === 'none') setMode('none');
                else { setPricingUnit(next as AllocationUnit); setMode('conversion'); }
              }}
            />
          </label>
          <label>
            <span>{t('orders:unitConversion.priceIn')}</span>
            <Select
              data-testid="conversion-price-unit"
              value={priceUnit}
              disabled={lockPricingUnit || legacyUnitReadOnly}
              getPopupContainer={getModalPopupContainer}
              options={(canConvertPhysicalUnit
                ? COMMERCIAL_UNITS.filter((unit) => (
                  !storedQuantitySet || getQuantitySetValue(storedQuantitySet, unit) !== null
                ))
                : [activePhysicalUnit]
              ).map((unit) => ({ value: unit, label: formatUnit(unit, language) }))}
              onChange={(next) => {
                setPriceUnit(next);
                setPieceRounding('none');
                setEditorDirty(true);
              }}
            />
          </label>
        </div>

        <label className="commercial-quantity-modal__amount">
          <span>{t('orders:unitConversion.amountInBaseUnit', { unit: formatUnit(activePhysicalUnit, language) })}</span>
          <InputNumber disabled={lockPhysicalQuantity || legacyUnitReadOnly} min={allowZero ? 0 : 0.001} max={amountMaximum} precision={3} step={0.001} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} value={amount} onChange={(next) => {
            setAmount(allowZero && Number(next) === 0 ? 0 : positive(next));
            setPieceRounding('none');
            setEditorDirty(true);
          }} />
        </label>

        {result.error ? (
          <Alert type="warning" showIcon message={t('orders:unitConversion.missingDimensions')} />
        ) : (
          <>
            {resultOutOfBounds && (
              <Alert type="warning" showIcon message={outOfBoundsMessage || t('orders:unitConversion.quantityLimit')} />
            )}
            {mode === 'conversion' && (
              <div className="commercial-quantity-modal__equation">
                <div><span>{t('orders:unitConversion.baseQuantity')}</span><strong>{formatQuantity(result.physicalQuantity)} {formatUnit(result.physicalUnit, language)}</strong></div>
                <RightOutlined />
                <div><span>{t('orders:unitConversion.convertedQuantity')}</span><strong>{formatQuantity(result.conversionQuantity || 0)} {formatUnit(result.conversionUnit || activeConversionUnit, language)}</strong></div>
              </div>
            )}
            {fractionalPieceRounding && (
              <div className="commercial-quantity-modal__rounding">
                <div className="commercial-quantity-modal__rounding-head">
                  <strong>{t('orders:unitConversion.fractionalTitle')}</strong>
                </div>
                <p>{t('orders:unitConversion.fractionalDescription')}</p>
                <div className="commercial-quantity-modal__rounding-options">
                  <button
                    type="button"
                    aria-label={t('orders:unitConversion.keepFractional')}
                    className={pieceRounding === 'none' ? 'active' : ''}
                    onClick={() => setPieceRounding('none')}
                  >
                    <span>{t('orders:unitConversion.keepFractional')}</span>
                    <strong>{formatQuantity(fractionalPieceRounding.exactPieces)} {formatUnit('pcs', language)}</strong>
                    <small>{formatQuantity(baseResult.physicalQuantity)} {formatUnit(baseResult.physicalUnit, language)}</small>
                  </button>
                  <button
                    type="button"
                    aria-label={t('orders:unitConversion.roundDown')}
                    disabled={!fractionalPieceRounding.down}
                    className={pieceRounding === 'down' ? 'active' : ''}
                    onClick={() => setPieceRounding('down')}
                  >
                    <span>{t('orders:unitConversion.roundDown')}</span>
                    <strong>{fractionalPieceRounding.down?.pieces || 0} {formatUnit('pcs', language)}</strong>
                    <small>{formatQuantity(fractionalPieceRounding.down?.physicalQuantity || 0)} {formatUnit(baseResult.physicalUnit, language)}</small>
                  </button>
                  <button
                    type="button"
                    aria-label={t('orders:unitConversion.roundUp')}
                    disabled={!fractionalPieceRounding.up}
                    className={pieceRounding === 'up' ? 'active' : ''}
                    onClick={() => setPieceRounding('up')}
                  >
                    <span>{t('orders:unitConversion.roundUp')}</span>
                    <strong>{fractionalPieceRounding.up?.pieces || 0} {formatUnit('pcs', language)}</strong>
                    <small>{formatQuantity(fractionalPieceRounding.up?.physicalQuantity || 0)} {formatUnit(baseResult.physicalUnit, language)}</small>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default CommercialQuantityEditor;
